import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Sparkles } from 'lucide-react';
import { ARCHETYPES } from './archetypeData';

const SaveState = ({ archetypeId, onSave, onBack }) => {
  const archetype = ARCHETYPES[archetypeId];
  const defaultName = `${archetype?.name || 'My'} State`;
  const [stateName, setStateName] = useState(defaultName);

  const suggestions = [
    `${archetype?.name} State`,
    `My ${archetype?.name} Reality`,
    `${archetype?.name} Mode`
  ];

  const handleSave = () => {
    onSave(stateName.trim() || defaultName);
  };

  return (
    <div className="fixed inset-0 bg-[#0a0a12] flex flex-col z-50">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 py-8">
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={onBack}
          className="flex items-center gap-2 text-white/40 hover:text-white/60 transition-colors mb-8"
        >
          <ChevronLeft size={20} />
          <span className="text-sm">Back</span>
        </motion.button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/30">
            <Sparkles size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-light text-white mb-2">
            Name Your Reality
          </h1>
        </motion.div>

        {/* Input field */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <input
            type="text"
            value={stateName}
            onChange={(e) => setStateName(e.target.value)}
            placeholder="Enter state name..."
            className="w-full px-5 py-4 rounded-xl bg-white/5 border border-white/10 text-white text-lg text-center focus:border-purple-500/50 focus:outline-none placeholder:text-white/30 transition-colors"
            data-testid="state-name-input"
          />
        </motion.div>

        {/* Suggestions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-10"
        >
          <p className="text-white/40 text-xs uppercase tracking-widest mb-3 text-center">
            Suggestions
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => setStateName(suggestion)}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  stateName === suggestion
                    ? 'bg-purple-500/30 text-white border border-purple-500/50'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Fixed Save Button */}
      <div className="flex-shrink-0 p-6 pb-24 bg-gradient-to-t from-[#0a0a12] via-[#0a0a12] to-transparent">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-3"
        >
          <button
            onClick={handleSave}
            disabled={!stateName.trim()}
            className={`w-full py-4 rounded-2xl font-medium transition-all ${
              stateName.trim()
                ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow-lg shadow-purple-500/30'
                : 'bg-white/10 text-white/40 cursor-not-allowed'
            }`}
            data-testid="save-state-btn"
          >
            Save & Listen
          </button>
          <p className="text-center text-white/30 text-xs">
            Your reality will be ready to play instantly
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SaveState;
